import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchOrganizationClientsSpecial } from "../../../Apis/Client.js";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AlarmAddIcon from "@mui/icons-material/AlarmAdd";
import LogoutIcon from "@mui/icons-material/Logout";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { getCoreScopeKey } from "../../../Configurations/coreSession";
import ListSummaryCards, {
  distinctCount,
} from "../../../Components/List/ListSummaryCards";

export const DEFAULT_COLUMNS = [
  {
    field: "client_name",
    label: "Client Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "client_since",
    label: "Client since",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "country",
    label: "Client country",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_clients_id",
  sortOrder: "desc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};

  function OrganizationClientList() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);

  const { userData } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const gridScopeKey = getCoreScopeKey(userData);

  function formatDate(date) {
    if (!date || date === "-") return null;

    const d = new Date(date);
    if (isNaN(d.getTime())) return null;

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  }

  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      try {
        const configRes = await fetch(
          `${MAIN_URL}/api/general-datagrids`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `organization_company_clients_grid_${gridScopeKey}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [gridScopeKey]);

  useEffect(() => {
    if (!hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_client_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const response = await fetch(
          `${MAIN_URL}/api/client-management/organization-clients/filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;

            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          const oldData = await fetchOrganizationClientsSpecial();
          data = oldData?.company_clients?.data || [];
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        let filteredData = data.filter((item) => {
          const unassignedOn = item.unassigned_on;
          return !unassignedOn || unassignedOn === "-" || unassignedOn === "";
        });

        let b = filteredData.map((item) => {
          const internName =
            item.intern_name ||
            (item.intern?.org_persons?.person
              ? `${item.intern.org_persons.person.first_name || ""} ${item.intern.org_persons.person.middle_name || ""} ${item.intern.org_persons.person.last_name || ""}`.trim()
              : item.intern
                ? `${item.intern.first_name || ""} ${item.intern.middle_name || ""} ${item.intern.last_name || ""}`.trim()
                : "");

          const internCode =
            item.intern_code ||
            (item.intern?.org_persons?.person_code || item.intern?.person_code || "");

          const functionRole =
            item.functional_role ||
            item.function_role ||
            (item.function_role?.functional_role_name || "-");

          const roleSpecialization =
            item.functional_role_specialization ||
            item.role_specialization ||
            (item.role_specialization?.functional_role_specialization_name || "-");

          const assignedOnValue =
            item.assigned_on && item.assigned_on !== "-"
              ? formatDate(item.assigned_on)
              : "";

          return {
            ...item,
            id: item.intern_functional_role_id,
            intern_name: internName,
            intern_code: internCode,
            function_role: functionRole,
            role_specialization: roleSpecialization,
            is_active: item.is_active == 1 || item.is_active === true ? "✔" : "✖",
            assigned_on: assignedOnValue,
          };
        });
        setClients(b);
      } catch (err) {
        if (err.name === "AbortError") return;

        if (err.response && err.response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to fetch intern functional roles:", err);
          toast.error("Failed to fetch intern functional roles");
          setClients([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [searchParams]);

  const deleteRole = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/client-management/organization-clients/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          toast.success(data.message || "Organization company client deleted successfully");
          setSearchParams((prev) => new URLSearchParams(prev));
        } else {
          const errorMessage =
            data.message ||
            data.error ||
            data.errors?.[Object.keys(data.errors || {})[0]]?.[0] ||
            "Failed to delete organization company client";
          toast.error(errorMessage);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          toast.error(
            error.response?.data?.error || "Failed to delete organization company client"
          );
        }
        console.error("Delete failed:", error);
      }
    },
    [setSearchParams]
  );


  const handleEdit = useCallback(
    (item) => {
      navigate(`/organization/company-clients/edit/${item.id || item.organization_client_id}`);
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(`/organization/company-clients/view/${item.id || item.organization_client_id}`);
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const summaryCards = useMemo(
    () => [
      {
        key: "total",
        label: "Total company clients",
        value: paginationData?.total ?? clients.length,
        iconKey: "users",
      },
      {
        key: "countries",
        label: "Countries on page",
        value: distinctCount(clients, "country"),
        iconKey: "building",
      },
      {
        key: "named",
        label: "Named on page",
        value: distinctCount(clients, "client_name"),
        iconKey: "hash",
      },
    ],
    [clients, paginationData]
  );

  return (
    <>
      <Layout4
        loading={loading}
        delete_action={"ORGANIZATION_CLIENT_DELETE"}
            heading={"Organization Clients"}
        add_action={"ORGANIZATION_CLIENT_ADD"}
        btnName={"Add Clients"}
        Data={clients}
       
        Icons={[
          <AutorenewIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <NextWeekIcon color="primary" />,
          <AlarmAddIcon sx={{ color: "text.secondary" }} />,
          <LogoutIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Organization Clients",
          "Organization Clients",
          "Add Organization Clients",
          "Organization Clients",
        ]}
        Route={"/organization/company-clients"}
            setData={setClients}
        DeleteFunc={deleteRole}
      />

      <ListSummaryCards cards={summaryCards} />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_clients"
          primaryKey="organization_clients_id"
          heading="Organization Clients"
          data={clients}
          sortname="organization_clients_id"
          showActions={false}
          Route="/organization/company-clients"
          DeleteFunc={deleteRole}
          EditFunc={handleEdit}
          edit_delete_action={["ORGANIZATION_COMPANY_CLIENT_EDIT", "ORGANIZATION_COMPANY_CLIENT_DELETE"]}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          filterModule="organization_clients"
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default OrganizationClientList;
